import React, { useState, useEffect, useCallback } from "react";
import { X, AlertCircle, Copy, Check, Code2 } from "lucide-react";
import { toast } from "react-toastify";
import WhatsAppNewPreview from "./WhatsAppNewPreview";
import { extractVariablesFromTemplateJson } from "../utils/templateUtils";
import { DEFAULT_TEMPLATE_LANGUAGE } from "../constants/templateLanguages";

const ViewEditTemplateModal = ({ isOpen, onClose, template }) => {
  const [previewData, setPreviewData] = useState(null);
  const [jsonCopied, setJsonCopied] = useState(false);

  const buildApiSendPayload = useCallback(() => {
    if (!template) return null;
    const templateJson = template.template_json || {};
    const comps = templateJson.components || [];
    const paramFormat = template.parameter_format || templateJson.parameter_format || "numbered";
    const isNamed = paramFormat === "named";
    const isAuth = (template.category || "").toUpperCase() === "AUTHENTICATION";

    const headerNamedNames = [];
    const bodyNamedNames = [];
    if (isNamed) {
      const hdr = comps.find(c => c.type === "HEADER");
      (hdr?.example?.header_text_named_params || []).forEach(p => headerNamedNames.push(p.param_name));
      const bdy = comps.find(c => c.type === "BODY");
      (bdy?.example?.body_text_named_params || []).forEach(p => bodyNamedNames.push(p.param_name));
    }

    const apiComponents = [];
    const BUTTON_SUB_TYPE_MAP = { QUICK_REPLY: "quick_reply", URL: "url", PHONE_NUMBER: "voice_call", COPY_CODE: "copy_code", OTP: "url" };

    // --- HEADER ---
    const headerComp = comps.find(c => c.type === "HEADER");
    if (headerComp) {
      if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComp.format)) {
        const mediaType = headerComp.format.toLowerCase();
        apiComponents.push({
          type: "header",
          parameters: [{ type: mediaType, [mediaType]: { link: "MEDIA_URL_HERE" } }],
        });
      } else if (headerComp.format === "TEXT" && headerComp.text) {
        const namedParams = headerComp.example?.header_text_named_params;
        const numberedSamples = headerComp.example?.header_text;
        if (namedParams && Array.isArray(namedParams) && namedParams.length > 0) {
          apiComponents.push({
            type: "header",
            parameters: namedParams.map((p, i) => {
              const param = { type: "text", text: p.example || "HEADER_VAR" };
              if (isNamed && headerNamedNames[i]) param.parameter_name = headerNamedNames[i];
              return param;
            }),
          });
        } else if (numberedSamples && numberedSamples.length > 0) {
          apiComponents.push({
            type: "header",
            parameters: numberedSamples.map(s => ({ type: "text", text: s })),
          });
        } else {
          const vars = headerComp.text.match(/\{\{[^}]+\}\}/g) || [];
          if (vars.length > 0) {
            apiComponents.push({
              type: "header",
              parameters: vars.map(() => ({ type: "text", text: "HEADER_VAR" })),
            });
          }
        }
      }
    }

    // --- BODY ---
    const bodyComp = comps.find(c => c.type === "BODY");
    if (bodyComp) {
      if (isAuth && bodyComp.add_security_recommendation !== undefined) {
        apiComponents.push({
          type: "body",
          parameters: [{ type: "text", text: "YOUR_OTP_CODE" }],
        });
      } else {
        const bodyVars = extractVariablesFromTemplateJson(templateJson).filter(v => v.target === "body");
        if (bodyVars.length > 0) {
          const namedParams = bodyComp.example?.body_text_named_params;
          if (namedParams && Array.isArray(namedParams) && namedParams.length > 0) {
            apiComponents.push({
              type: "body",
              parameters: namedParams.map((p, i) => {
                const param = { type: "text", text: p.example || bodyVars[i]?.sample || "BODY_VAR" };
                if (isNamed && bodyNamedNames[i]) param.parameter_name = bodyNamedNames[i];
                return param;
              }),
            });
          } else {
            apiComponents.push({
              type: "body",
              parameters: bodyVars.map((v, i) => ({ type: "text", text: v.sample || `BODY_VAR_${i + 1}` })),
            });
          }
        }
      }
    }

    // --- BUTTONS ---
    const buttonsComp = comps.find(c => c.type === "BUTTONS");
    console.log("buttonsComp", buttonsComp);
    if (buttonsComp?.buttons) {
      buttonsComp.buttons.forEach((btn, btnIdx) => {
        const parameters = [];

        if (btn.type === "QUICK_REPLY") {
          parameters.push({ type: "payload", payload: btn.text || "REPLY" });
        } else if (btn.type === "URL") {
          if (!btn.url || !btn.url.includes("{{")) return;
          parameters.push({ type: "text", text: btn.example?.[0] || "URL_DYNAMIC_SUFFIX" });
        } else if (btn.type === "COPY_CODE" || btn.type === "COPY_OFFER_CODE") {
          const code = btn.example?.[0] || btn.example || "YOUR_CODE";
          parameters.push({ type: "coupon_code", coupon_code: typeof code === "string" ? code : "YOUR_CODE" });
        } else if (btn.type === "OTP") {
          parameters.push({ type: "coupon_code", coupon_code: "YOUR_OTP_CODE" });
        } else if (btn.type === "PHONE_NUMBER") {
          return;
        }

        apiComponents.push({
          type: "button",
          sub_type: BUTTON_SUB_TYPE_MAP[btn.type] || btn.type.toLowerCase(),
          index: String(btnIdx),
          parameters,
        });
      });
    }

    return {
      phone_number: "YOUR_PHONE_NUMBER",
      to: "RECIPIENT_PHONE_NUMBER",
      template_name: template.name,
      language: template.language || DEFAULT_TEMPLATE_LANGUAGE,
      components: apiComponents,
    };
  }, [template]);

  const handleCopyCurl = useCallback(() => {
    const payload = buildApiSendPayload();
    if (!payload) return;
    const jsonBody = JSON.stringify(payload, null, 2);
    const curlCmd = `curl -X POST https://apis-ping.blutec.ai/api/v1/messages/template \\\n  -H "X-API-Key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '${jsonBody}'`;
    navigator.clipboard.writeText(curlCmd);
    setJsonCopied(true);
    toast.success("cURL command copied!");
    setTimeout(() => setJsonCopied(false), 2500);
  }, [buildApiSendPayload]);

  const handleCopyJson = useCallback(() => {
    const payload = buildApiSendPayload();
    if (!payload) return;
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    setJsonCopied(true);
    toast.success("JSON payload copied!");
    setTimeout(() => setJsonCopied(false), 2500);
  }, [buildApiSendPayload]);

  useEffect(() => {
    if (template) {
      // Transform template data to match WhatsAppNewPreview expected format
      const templateJson = template.template_json || {};
      const components = templateJson.components || [];
      
      // Extract body from BODY component
      const bodyComponent = components.find(c => c.type === 'BODY');
      const body = bodyComponent?.text || template.body || '';
      
      // Extract footer from FOOTER component
      const footerComponent = components.find(c => c.type === 'FOOTER');
      const footer = footerComponent?.text || template.footer || '';
      
      // Extract header from HEADER component
      const headerComponent = components.find(c => c.type === 'HEADER');
      const headerFormat = headerComponent?.format || template.header_type || template.headerFormat || 'NONE';
      const headerText = headerComponent?.text || template.headerText || '';
      const headerExample = headerComponent?.example || {};
      const headerMediaUrl =  template.header_media_url || null;

      // Extract buttons from BUTTONS component
      const buttonsComponent = components.find(c => c.type === 'BUTTONS');
      const buttons = buttonsComponent?.buttons || template.buttons || [];
      
      // Extract variables properly from template_json using utility function
      const extractedVariables = extractVariablesFromTemplateJson(templateJson);
      
      // If no variables extracted, try to get from template.variables
      const variables = extractedVariables.length > 0 
        ? extractedVariables.map((v, index) => ({
            id: v.id || index + 1,
            name: v.name,
            sample: v.sample || `Sample ${v.name}`,
            target: v.target || "body"
          }))
        : (template.variables || []).map((v, index) => ({
            id: v.id || index + 1,
            name: v.name || String(v.id || index + 1),
            sample: v.sample || `Sample ${v.name || index + 1}`,
            target: v.target || "body"
          }));
      
      // Determine template type
      let templateType = 'text';
      if (buttons.length > 0) {
        templateType = 'buttons';
      } else if (headerFormat && headerFormat !== 'NONE' && headerFormat !== 'TEXT') {
        templateType = headerFormat.toLowerCase();
      }
      
      // Ensure headerFormat is always a string
      const finalHeaderFormat = String(headerFormat || 'NONE');
      const finalHeaderType = finalHeaderFormat.toLowerCase();
      
      setPreviewData({
        name: template.name || '',
        category: template.category || 'marketing',
        language: template.language || DEFAULT_TEMPLATE_LANGUAGE,
        templateType: template.template_type || templateType,
        headerFormat: finalHeaderFormat,
        headerType: finalHeaderType,
        headerText: headerText,
        header_media_url: headerMediaUrl,
        body: body,
        footer: footer,
        buttons: buttons.map((btn, idx) => ({
          id: idx + 1,
          type: btn.type,
          text: btn.text,
          url: btn.url,
          phone: btn.phone_number
        })),
        variables: variables
      });
    }
  }, [template, isOpen]);

  if (!isOpen || !template) return null;

  const templateJson = template.template_json || {};
  const components = templateJson.components || [];
  const bodyComponent = components.find(c => c.type === 'BODY');
  const footerComponent = components.find(c => c.type === 'FOOTER');
  const headerComponent = components.find(c => c.type === 'HEADER');
  const buttonsComponent = components.find(c => c.type === 'BUTTONS');

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
            <div>
              <h3 className="text-base font-bold text-gray-900">
                Template Preview
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Template: {template.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X size={16} className="text-gray-600" />
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto p-3"
            style={{ maxHeight: "calc(90vh - 120px)" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* LEFT: Template Details */}
              <div className="lg:col-span-2 space-y-3">
                {/* Basic Info */}
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <h4 className="text-xs font-bold text-gray-900 mb-2">
                    Template Information
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                        Template Name
                      </label>
                      <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                        {template.name}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                          Category
                        </label>
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg capitalize">
                          {template.category}
                        </p>
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                          Language
                        </label>
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                          {template.language}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                          Status
                        </label>
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg capitalize">
                          {template.status}
                        </p>
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-gray-700 mb-1">
                          Parameter Type
                        </label>
                        <p className="text-xs bg-gray-50 px-2 py-1.5 rounded-lg">
                          {(template.parameter_format || template.template_json?.parameter_format || "numbered") === "named" ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase">Named</span>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold uppercase">Numbered</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Header */}
                {headerComponent && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">
                      Header
                    </h4>
                    <div className="space-y-1.5">
                      <p className="text-xs text-gray-600">
                        <span className="font-semibold">Type:</span> {headerComponent.format || 'NONE'}
                      </p>
                      {headerComponent.text && (
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                          {headerComponent.text}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Body */}
                {bodyComponent && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">
                      Message Body
                    </h4>
                    <div className="space-y-2">
                      {previewData?.variables && previewData.variables.length > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-gray-700 mb-1.5">
                            Variables:
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {previewData.variables.map((variable) => (
                              <div
                                key={variable.id}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-[#25D366]/10 text-[#25D366] rounded-lg text-xs font-medium border border-[#25D366]/20"
                              >
                                <span>{`{{${variable.name}}}`}</span>
                                <span className="text-[10px] text-gray-600">
                                  ({variable.sample})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-gray-900 bg-gray-50 px-2 py-2 rounded-lg whitespace-pre-wrap">
                          {bodyComponent.text}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer */}
                {footerComponent && footerComponent.text && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">
                      Footer
                    </h4>
                    <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                      {footerComponent.text}
                    </p>
                  </div>
                )}

                {/* Buttons */}
                {buttonsComponent && buttonsComponent.buttons && buttonsComponent.buttons.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <h4 className="text-xs font-bold text-gray-900 mb-2">
                      Buttons ({buttonsComponent.buttons.length})
                    </h4>
                    <div className="space-y-2">
                      {buttonsComponent.buttons.map((button, index) => (
                        <div
                          key={index}
                          className="p-2 border border-gray-200 rounded-lg space-y-1.5"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-gray-700">
                              Button {index + 1} - {button.type.replace("_", " ")}
                            </span>
                          </div>
                          <p className="text-xs text-gray-900 bg-gray-50 px-2 py-1.5 rounded-lg">
                            {button.text}
                          </p>
                          {button.type === "URL" && button.url && (
                            <p className="text-[10px] text-gray-600">
                              URL: <a href={button.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{button.url}</a>
                            </p>
                          )}
                          {button.type === "PHONE_NUMBER" && button.phone_number && (
                            <p className="text-[10px] text-gray-600">
                              Phone: {button.phone_number}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejection Reason */}
                {template.status === "rejected" && template.rejected_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={14} />
                      <div>
                        <p className="text-xs font-semibold text-red-900 mb-0.5">
                          Rejection Reason
                        </p>
                        <p className="text-xs text-red-700">
                          {template.rejected_reason}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT: WhatsApp Live Preview */}
              <div className="lg:col-span-1">
                <div className="sticky top-0">
                  {previewData && (
                    <WhatsAppNewPreview templateData={previewData} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between gap-2 p-2 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyCurl}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#048AAD]/30 text-[#048AAD] bg-[#048AAD]/5 hover:bg-[#048AAD]/10 transition-colors"
              >
                {jsonCopied ? <Check size={13} /> : <Code2 size={13} />}
                {jsonCopied ? "Copied!" : "Copy cURL"}
              </button>
              <button
                onClick={handleCopyJson}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Copy size={13} />
                Copy JSON
              </button>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewEditTemplateModal;
